"use client";

import { useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import LegalFooter from "@/components/LegalFooter";

type Step = "form" | "verify" | "done";

export default function RegisterPage() {
  return (
    <Suspense fallback={null}>
      <RegisterPageInner />
    </Suspense>
  );
}

function RegisterPageInner() {
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [step, setStep] = useState<Step>("form");
  const [method, setMethod] = useState<"phone" | "email">("phone");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    birthDate: "",
    parentEmail: "",
  });
  const [consent, setConsent] = useState(false);
  const [userId, setUserId] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  function update(field: string, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        birthDate: form.birthDate,
        parentEmail: form.parentEmail,
        inviteToken,
      };
      if (method === "phone") payload.phone = form.phone;
      else payload.email = form.email;
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setUserId(data.userId);
      if (data.devCode) setDevCode(data.devCode);
      setStep("verify");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Fehler");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStep("done");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Fehler");
    } finally {
      setLoading(false);
    }
  }

  if (step === "done") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Fast geschafft!</h2>
          <p className="text-gray-600 text-sm mb-6">
            Deine Nummer ist bestätigt. Der Admin muss dein Konto noch freigeben. Du bekommst eine
            SMS, sobald du loslegen kannst!
          </p>
          <Link
            href="/login"
            className="inline-block bg-purple-600 text-white rounded-xl px-6 py-3 font-semibold hover:bg-purple-700 transition"
          >
            Zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">✨</div>
          <h1 className="text-2xl font-bold text-gray-800">Konto erstellen</h1>
          <p className="text-gray-500 text-sm mt-1">Sicherer Chat für Kinder</p>
        </div>

        {step === "form" ? (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dein Name</label>
              <input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Max Mustermann"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                required
              />
            </div>
            <div>
              <div className="flex bg-gray-100 rounded-xl p-1 mb-2">
                <button
                  type="button"
                  onClick={() => setMethod("phone")}
                  className={`flex-1 py-1.5 text-sm rounded-lg transition ${
                    method === "phone"
                      ? "bg-white shadow-sm text-gray-800 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  📱 Handy
                </button>
                <button
                  type="button"
                  onClick={() => setMethod("email")}
                  className={`flex-1 py-1.5 text-sm rounded-lg transition ${
                    method === "email"
                      ? "bg-white shadow-sm text-gray-800 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  ✉️ E-Mail
                </button>
              </div>
              {method === "phone" ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Handynummer
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+49 123 456789"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Du bekommst einen 6-stelligen Code per SMS.
                  </p>
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deine E-Mail-Adresse
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="du@beispiel.de"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Kein Handy? Kein Problem. Wir schicken den Code per E-Mail.
                    Idealerweise eine eigene Adresse — sonst frag bitte deine Eltern.
                  </p>
                </>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Geburtsdatum</label>
              <input
                type="date"
                value={form.birthDate}
                onChange={(e) => update("birthDate", e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail der Eltern
              </label>
              <input
                type="email"
                value={form.parentEmail}
                onChange={(e) => update("parentEmail", e.target.value)}
                placeholder="eltern@beispiel.de"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-400"
                required
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-gray-600 bg-purple-50 rounded-xl p-3 border border-purple-100">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 accent-purple-600 flex-shrink-0"
                required
              />
              <span>
                Meine Eltern wissen, dass ich mich anmelde, und sind einverstanden.
                Ich habe die{" "}
                <a
                  href="/legal/nutzungsbedingungen"
                  target="_blank"
                  className="text-purple-600 underline"
                >
                  Nutzungsbedingungen
                </a>{" "}
                und die{" "}
                <a
                  href="/legal/datenschutz"
                  target="_blank"
                  className="text-purple-600 underline"
                >
                  Datenschutzerklärung
                </a>{" "}
                gelesen.
              </span>
            </label>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading || !consent}
              className="w-full bg-purple-600 text-white rounded-xl py-3 font-semibold hover:bg-purple-700 transition disabled:opacity-50"
            >
              {loading ? "..." : "Registrieren"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <p className="text-sm text-gray-600 text-center">
              Code an <strong>{method === "phone" ? form.phone : form.email}</strong> gesendet
            </p>
            {devCode && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                <p className="text-xs text-yellow-700 font-medium">Testmodus — kein SMS</p>
                <p className="text-2xl font-bold text-yellow-800 tracking-widest">{devCode}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                6-stelliger Code
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-purple-400"
                required
              />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white rounded-xl py-3 font-semibold hover:bg-purple-700 transition disabled:opacity-50"
            >
              {loading ? "..." : "Bestätigen"}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-gray-500 mt-6">
          Schon ein Konto?{" "}
          <Link href="/login" className="text-purple-600 hover:underline font-medium">
            Anmelden
          </Link>
        </p>
      </div>
      <LegalFooter />
    </div>
  );
}
